import moment from '@utils/moment-wrapper';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import defaults from 'lodash/defaults';
import union from 'lodash/union';
import uniq from 'lodash/uniq';
import pickBy from 'lodash/pickBy';
import countBy from 'lodash/countBy';
import orderBy from 'lodash/orderBy';
import defaultsDeep from 'lodash/defaultsDeep';
import some from 'lodash/some';
import omit from 'lodash/omit';
import replace from 'lodash/replace';
import trim from 'lodash/trim';
import { prettyTime } from '@/utils/formatters.js';
import {
    MEASURE_OPTIONS,
    getLabelFromTargetGroupMeasure,
} from '../query-builder/query-selectors/query-selector-constants/query-selector-options';
import { DEVICE_PROP_FIELDS } from '@/constants/device-props';
import { getAppIconType } from '@/utils/apps';
import lowerFirst from 'lodash/lowerFirst';
import { apiLevelToVersion } from '@utils/android-versions';
import {
    FilterType,
    DateConfigTypes,
    EventName,
    EventKind,
    MetadataTypeId,
    TimeInterval,
    TimeSeriesPeriod,
} from '@/types/analytics/analytics.types';
import { PageParameterId } from '@pendo/middleware';
import {
    buildFormulaEquationString,
    migrateLegacyFormulas,
    getFormulaGroupIds,
} from '@/data-explorer/query-builder/query-selectors/formula-builder/formula.utils';
import {
    isDraftSegment,
    generateNewDraftSegmentId,
} from '@/data-explorer/query-builder/query-selectors/segment-selector/helpers/segment-selector.helper';
import { formatStartEndDates } from '@/common/time-series-picker/time-series-picker.util';
import { eventTypeConfig as TYPE_SPECIFIC_CONSTANTS } from '@pendo/middleware';
import { createNewTargetGroupObject } from '@/data-explorer/query-builder/query-selectors/target-selector/target-selectors.utils';
import { getActiveApplicationsMap } from '@/data/session/session-globals.ts';

export const MAX_SERIES_VISIBLE = 10;

/** Threshold until the source builder agg uses the merge file instead of the single file */
export const MAX_SINGLE_TARGETS = 10;

// These DX versions indicate which features are currently in use and are
// separate from the DX schema version.
export const DX_VERSION = 5;

export const DX_VERSIONS = {
    0: 'classicDX',
    1: 'timeToValueDX',
    2: 'epFilteringDX',
    3: 'multiDimensionalGroupByDX',
    4: 'reportLevelFilteringDX',
    5: 'calculationsForDX',
};

export const DX_REPORT_KEY = '_pendo_dx_report';

export const MAX_CARDINALITY_LIMIT = 500_000;

export function hasMultipleGroupMeasures (targetGroups = []) {
    if (targetGroups.length === 0) {
        return false;
    }

    const firstMeasure = targetGroups[0].measure.value.name;

    return targetGroups.some(({ measure }) => measure.value.name !== firstMeasure);
}

export function hasMultipleEventTypes (targetGroups = []) {
    const allTargets = targetGroups.flatMap(({ targets }) => targets);
    if (allTargets.length === 0) {
        return false;
    }

    const firstType = allTargets[0].type;

    return allTargets.some(({ type }) => type !== firstType);
}

/**
 * Checks target groups for any targets with mismatched types or measurement
 * specifications. Only when all targets are of the same type, and are measured
 * the same way, can we consolidate DX chart selections for ease of reporting.
 */
export function hasMultipleTargetSpecs (targetGroups, globals) {
    if (globals.formulas?.length) {
        return true;
    }

    return hasMultipleGroupMeasures(targetGroups) || hasMultipleEventTypes(targetGroups);
}

export function generateDataSourceName (firstGroupType, measure) {
    const typeLabel = TYPE_SPECIFIC_CONSTANTS[firstGroupType]?.label;
    const measureLabel = getLabelFromTargetGroupMeasure(measure);
    const measureName = measure?.value?.name;
    const isAnyActivity = !!TYPE_SPECIFIC_CONSTANTS[firstGroupType]?.isAnyActivity;

    if (
        measureName === MEASURE_OPTIONS.RAGE_CLICKS.name ||
        measureName === MEASURE_OPTIONS.ERROR_CLICKS.name ||
        measureName === MEASURE_OPTIONS.U_TURNS.name
    ) {
        return `${measureLabel}`;
    }

    if (measureName === MEASURE_OPTIONS.NUM_MINUTES.name) {
        return 'Total time';
    }

    if (measureName === MEASURE_OPTIONS.AVERAGE_MINUTES_PER_VISITOR.name) {
        return 'Average time per visitor';
    }

    if (measureName === MEASURE_OPTIONS.AVERAGE_MINUTES_PER_ACCOUNT.name) {
        return 'Average time per account';
    }

    const unadornedSourceMeasures = [MEASURE_OPTIONS.TOTAL.name, MEASURE_OPTIONS.MEDIAN.name];

    if (unadornedSourceMeasures.includes(measureName)) {
        // remove "events" word from the measure to avoid redundancy when combined with the label
        const updatedMeasure = trim(replace(measureLabel, 'events', ''));

        return `${updatedMeasure} ${typeLabel}`;
    }

    if (isAnyActivity) {
        if (measureName === MEASURE_OPTIONS.NUM_VISITORS.name || measureName === MEASURE_OPTIONS.NUM_ACCOUNTS.name) {
            return `${measureLabel} with any activity`;
        }

        return `${measureLabel} related to any activity`;
    }

    return `${measureLabel} with ${typeLabel}`;
}

export function sortResultRows (rows, hasMultipleDateConfigs) {
    if (hasMultipleDateConfigs) {
        return orderBy(rows, ['seriesName'], ['desc']);
    }

    return orderBy(rows, ['aggregate'], ['desc']);
}

export function shouldHideNoActivityRows ({
    hasDataExplorerZeroActivityRemovalFlag,
    groupings,
    hasFormulas,
    hideNoActivityRowsSetting,
}) {
    // When no groupings exist, zero-activity rows must always be shown
    if (!groupings || groupings.length === 0) return false;
    // When in the segment flag is enabled and groups exist, always hide zero rows
    if (hasDataExplorerZeroActivityRemovalFlag) return true;
    // When multiple groupings exist, always hide zero rows
    if (groupings?.length > 1) return true;
    // When we have groups and formulas, always hide zero rows
    if (hasFormulas) return true;

    // Single grouping: respect the report's saved setting (or true if undefined)
    return hideNoActivityRowsSetting !== undefined ? hideNoActivityRowsSetting : true;
}

export function fillRowSelections (selections, rows, opts, hideNoActivityRows, hasFormulas = false) {
    if (isEmpty(rows)) return selections;

    const visibilitySelectionType = get(selections, 'globals.visibilitySelection.type', 'custom');
    const maxSelections = get(selections, 'globals.visibilitySelection.count', MAX_SERIES_VISIBLE);
    const selectedRowCount = countBy(rows, ({ id }) => selections[id]).true || 0;
    let totalVisible = 0;

    const { visibilityProp } = defaults(opts, {
        visibilityProp: null,
    });

    const { globals } = selections;
    const rowVisibility = { globals };

    rows.forEach((row) => {
        let visible;
        const rowHasData = row.aggregate > 0 || row.yAxisSum > 0;

        // you've reached maximum visible
        // OR you're hiding no activity rows and row has no data.
        if (totalVisible >= maxSelections || (!rowHasData && hideNoActivityRows)) {
            visible = false;
        }
        // (you haven't reached maximum ^) and:
        // you are in top 10 mode
        // OR all rows were hidden
        else if (visibilitySelectionType === 'top' || !selectedRowCount) {
            // fill hides events if report has formulas
            visible = !(hasFormulas && !row.formulaId);
        }
        // custom visibility mode, keep previous visibility
        else {
            visible = !!selections[row.id];
        }

        // add to rowVisibility object
        if (visible) {
            totalVisible++;
            rowVisibility[row.id] = visible;
        }

        // set visibility prop on each row.
        if (visibilityProp) {
            if (!row[visibilityProp] && visible) {
                row[visibilityProp] = visible;
            } else if (row[visibilityProp] && !visible) {
                delete row[visibilityProp];
            }
        }
    });

    return rowVisibility;
}

export function cleanseGroupValues ({ groupValue }) {
    const groupValueRegexd = /(.*) (\(.*\))/.exec(groupValue);

    return groupValueRegexd && groupValueRegexd.length ? groupValueRegexd[1] : groupValue;
}

export function attachIcon (series, appIcons) {
    const iconArr = appIcons.filter((app) => app.id === series.targetId);
    if (iconArr.length) {
        series.icon = iconArr[0].icon;
    }

    return series;
}

/**
 * you can pass measure name or label as the first parameter
 * ex. 'percentVisitors' or 'Percent of visitors'
 * **/
export function isPercentage (measure, format) {
    return (
        [
            MEASURE_OPTIONS.PERCENT_VISITORS.label,
            MEASURE_OPTIONS.PERCENT_VISITORS.name,
            MEASURE_OPTIONS.PERCENT_ACCOUNTS.label,
            MEASURE_OPTIONS.PERCENT_ACCOUNTS.name,
        ].includes(measure) || format === 'percent'
    );
}

export function isTimeMeasure (measure) {
    return [
        MEASURE_OPTIONS.NUM_MINUTES.label,
        MEASURE_OPTIONS.NUM_MINUTES.name,
        MEASURE_OPTIONS.AVERAGE_MINUTES_PER_VISITOR.label,
        MEASURE_OPTIONS.AVERAGE_MINUTES_PER_VISITOR.name,
        MEASURE_OPTIONS.AVERAGE_MINUTES_PER_ACCOUNT.label,
        MEASURE_OPTIONS.AVERAGE_MINUTES_PER_ACCOUNT.name,
    ].includes(measure);
}

export function formatNumberAsPercentage (value) {
    return value.toLocaleString(undefined, {
        style: 'percent',
        maximumFractionDigits: 2,
    });
}

export function buildDateConfigGroup ({ dateConfig, primaryDateConfig }) {
    const dates = getDatesFromDateConfig({
        dateConfig,
        primaryDateConfig,
    });
    if (!dates) {
        return `UNPARSEABLE DATE RANGE: ${JSON.stringify({
            type: get(dateConfig, 'value?.dateRange?.type'),
            key: get(dateConfig, 'value?.dateRange?.key'),
        })}`;
    }
    let { start, end } = dates;
    if (!end) end = start;
    const configType = get(dateConfig, 'value.dateRange.type');
    const isOneDay = retrieveDayCountFromDateConfig(dateConfig) === 1;
    let dateString;

    const getDateConfig = () =>
        `${retrieveDayCountFromDateConfig(dateConfig)} Day${isOneDay ? '' : 's'} (${formatStartEndDates(start, end)})`;

    if (configType === 'Custom' && dateConfig.isPrimary) {
        dateString = getDateConfig();

        return dateString;
    }
    if (configType === 'Custom' && !dateConfig.isPrimary) {
        dateString = `Custom ${getDateConfig()}`;

        return dateString;
    }

    return `${configType} (${formatStartEndDates(start, end)})`;
}

export function getDatesFromDateConfig ({ dateConfig, primaryDateConfig }) {
    let dayCount = retrieveDayCountFromDateConfig(dateConfig);
    const range = get(dateConfig, 'value.dateRange.type');
    const primaryRange = get(primaryDateConfig, 'value.dateRange.type');
    const rangeType = get(dateConfig, 'value.dateRange.key');

    if (range === 'Custom') {
        const { start, end } = get(dateConfig, 'value.dateRange');

        return {
            start: moment(start, { ignoreTimeZone: true }),
            end: moment(end, { ignoreTimeZone: true }),
        };
    }
    if (range === 'Today') {
        return {
            start: moment(undefined, { ignoreTimeZone: true }),
            end: null,
        };
    }

    if (range === 'Yesterday') {
        return {
            start: moment(undefined, { ignoreTimeZone: true }).subtract(1, 'days'),
            end: null,
        };
    }

    if (range === `Last ${dayCount} Days`) {
        return {
            start: moment(undefined, { ignoreTimeZone: true }).subtract(dayCount, 'days'),
            end: moment(undefined, { ignoreTimeZone: true }).subtract(1, 'days'),
        };
    }

    if (rangeType === 'previousPeriod') {
        const nullifyEnd = dayCount === 1;
        if (primaryRange === 'Today') {
            dayCount = 0.5;
        }

        if (primaryRange === 'Custom') {
            const { start, end } = get(primaryDateConfig, 'value.dateRange');

            return {
                start: moment(start, { ignoreTimeZone: true }).subtract(dayCount, 'days'),
                end: nullifyEnd ? null : moment(end, { ignoreTimeZone: true }).subtract(dayCount, 'days'),
            };
        }

        return {
            start: moment(undefined, { ignoreTimeZone: true }).subtract(dayCount * 2, 'days'),
            end: nullifyEnd ? null : moment(undefined, { ignoreTimeZone: true }).subtract(dayCount + 1, 'days'),
        };
    }

    if (rangeType === 'samePeriodLastYear') {
        const nullifyEnd = dayCount === 1;
        if (primaryRange === 'Today') {
            dayCount = 0;
        }

        if (primaryRange === 'Custom') {
            const { start, end } = get(primaryDateConfig, 'value.dateRange');

            return {
                start: moment(start, { ignoreTimeZone: true }).subtract(1, 'year'),
                end: nullifyEnd ? null : moment(end, { ignoreTimeZone: true }).subtract(1, 'year'),
            };
        }

        return {
            start: moment(undefined, { ignoreTimeZone: true })
                .subtract(1, 'year')
                .subtract(dayCount, 'days'),
            end: nullifyEnd
                ? null
                : moment(undefined, { ignoreTimeZone: true })
                    .subtract(1, 'year')
                    .subtract(1, 'day'),
        };
    }

    return undefined;
}

export function retrieveDayCountFromDateConfig (dateConfig) {
    const range = get(dateConfig, 'value.dateRange.type');
    if (!range) {
        return null;
    }

    if (range === 'Custom') {
        const { start, end } = get(dateConfig, 'value.dateRange');

        return moment(end, { ignoreTimeZone: true }).diff(moment(start, { ignoreTimeZone: true }), 'days') + 1;
    }

    if (['Today', 'Yesterday'].includes(range)) {
        return 1;
    }

    return parseInt(/(Same|Previous|Last) (\d*) (Day(s)? Last Year|Day(s)?)/.exec(range)[2], 10);
}

export function eventPropNameMapper ({ kind, eventPropertyNameList, eventPropertyConfigurations }) {
    switch (kind) {
        case EventKind.TrackEvent:
            return eventPropertyNameList || [];
        case EventKind.Feature:
            if (!eventPropertyConfigurations) return [];

            return eventPropertyConfigurations.filter(({ isActive }) => isActive).map(({ name }) => name);
        default:
            return undefined;
    }
}

export function sharedEventPropNames (targets) {
    return union(...targets.map(eventPropNameMapper).filter(Boolean));
}

const uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

/**
 * A data source's target group ID should default to its data source ID.
 * Any target group IDs that aren't in UUID form are from an earlier iteration of development,
 * never released to customers but present in some developers' saved reports.
 * For ease of development and testing, we blow those old IDs away too.
 */
export function getTargetGroupId (dataSource) {
    if (!dataSource.targetGroupId || !uuidRegex.test(dataSource.targetGroupId.value)) {
        return dataSource.id.value;
    }

    return dataSource.targetGroupId.value;
}

export function getDxVersion () {
    const version = DX_VERSION;

    return {
        version,
        versionName: DX_VERSIONS[version],
    };
}

export function createEventPropertiesFromQuery ({
    targetGroups = [],
    globals,
    savedReport = false,
    repeatReport = false,
    onSave = false,
    hasExceededMaxThresholdCount = false,
    hasFormulasExceededMaxThresholdCount = false,
    duration = 0,
    rowCount = 0,
    totalRows = 0,
    visibleSeries = [],
    visibilityConfig = {},
    chartSelectionConfig = {},
    hideNoActivityRowsConfig = {},
}) {
    const SOURCE_MAP = {
        pageViews: 'page',
        featureClicks: 'feature',
        trackEvents: 'track event',
        guideViews: 'guide',
        anyActivity: 'app',
    };
    const exceededMaxThresholdCount = hasExceededMaxThresholdCount || hasFormulasExceededMaxThresholdCount;
    const hasSegment = globals.segments.filter((segment) => segment.value.id !== 'everyone').length > 0;
    const isSegmentComparison = globals.segments.length > 1;
    const hasGroupBy = !!globals.groupings?.length;
    const isDateComparison = globals.dateConfigs.length > 1;
    const numConfigurationOptions = [isDateComparison, hasSegment, hasGroupBy].filter(Boolean).length;

    const groupByEPs = getGroupByTypes(globals.groupings);
    const filterEPs = getFilterTypes(targetGroups, globals.filters);
    const formulaEPs = getFormulaInfo(globals.formulas, targetGroups);

    const primaryDateConfig = globals.dateConfigs.find((config) => config.isPrimary);
    const numDaysInDateRange = primaryDateConfig ? retrieveDayCountFromDateConfig(primaryDateConfig) : 0;

    const COUNTABLE_TYPES = ['pageViews', 'featureClicks', 'trackEvents'];
    let numCountables = 0;
    const countableTypesIncluded = {};

    const visibleRows = visibleSeries?.length || 0;
    const seriesEventTypesSet = new Set();
    for (const series of visibleSeries) {
        if (series.formulaId) {
            seriesEventTypesSet.add('formula');
        } else if (series.sourceType) {
            seriesEventTypesSet.add(SOURCE_MAP[series.sourceType]);
        }
    }
    const seriesEventTypes = Array.from(seriesEventTypesSet).join(', ') || null;

    const chartChooserSet = new Set();
    for (const [key, value] of Object.entries(chartSelectionConfig)) {
        if (key !== 'globals') {
            const chartType = value.stacked ? 'stacked '.concat(value.type) : value.type;
            chartChooserSet.add(chartType);
        }
    }

    const chartChooser = Array.from(chartChooserSet).join(', ') || null;

    // DX reports created before the introduction of the visibilitySelection will be custom by default
    const chartVisibility = visibilityConfig?.type || 'custom';
    const topValue = visibilityConfig?.count || null;

    const numEventsPerType = {
        pageViews: 0,
        featureClicks: 0,
        trackEvents: 0,
        guideViews: 0,
        anyActivity: 0,
    };

    const measurements = [];

    let hasNamedEventGroup = false;

    for (const targetGroup of targetGroups) {
        measurements.push(getLabelFromTargetGroupMeasure(targetGroup.measure));

        for (const { type } of targetGroup.targets) {
            ++numEventsPerType[type];

            if (COUNTABLE_TYPES.includes(type)) {
                ++numCountables;
                countableTypesIncluded[type] = true;
            }
        }

        if (targetGroup.name?.valid && targetGroup.name?.value) {
            hasNamedEventGroup = true;
        }
    }

    const numCountableTypes = Object.keys(countableTypesIncluded).length;
    const {
        pageViews: numPages,
        featureClicks: numFeatures,
        trackEvents: numTrackEvents,
        guideViews: numGuides,
        anyActivity: numApps,
    } = numEventsPerType;
    const hasPages = !!numPages;
    const hasFeatures = !!numFeatures;
    const hasTrackEvents = !!numTrackEvents;
    const hasGuides = !!numGuides;
    const hasApps = !!numApps;

    const primaryMeasurement = measurements[0] || 'None';
    const secondaryMeasurement = measurements[1] || 'None';
    const measurementsCount = new Set(measurements).size;

    const hideNoActivityRows = hideNoActivityRowsConfig.toggleVisible ? hideNoActivityRowsConfig.value : undefined;

    return {
        duration,
        rowCount,
        totalRows,
        visibleRows,
        savedReport,
        repeatReport,
        onSave,
        numCountables,
        numCountableTypes,
        hasPages,
        hasFeatures,
        hasTrackEvents,
        hasGuides,
        hasApps,
        numPages,
        numFeatures,
        numTrackEvents,
        numGuides,
        numApps,
        numConfigurationOptions,
        hasSegment,
        isSegmentComparison,
        ...groupByEPs,
        ...filterEPs,
        ...formulaEPs,
        hasGroupBy,
        isDateComparison,
        numDaysInDateRange,
        primaryMeasurement,
        secondaryMeasurement,
        measurementsCount,
        hasNamedEventGroup,
        chartChooser,
        chartVisibility,
        topValue,
        seriesEventTypes,
        hideNoActivityRows,
        exceededMaxThresholdCount,
    };
}

export function getGroupByTypes (groupings = [], filterByType = undefined, includeMetadataGroup = false) {
    const types = new Set();
    const values = new Set();
    const eventPropTypes = new Set();
    const metadataGroups = new Set();
    let hasGroupByDeviceProp = false;

    if (filterByType) {
        groupings = groupings.filter(({ type }) => type === filterByType);
    }

    groupings.forEach(({ type, configuration: groupByMetadata }) => {
        const { field, kind, group, DisplayName } = groupByMetadata;
        const groupByType = {
            visitor: 'visitor_metadata',
            account: 'account_metadata',
            parentAccount: 'parent_account_metadata',
            historical: 'historical_metadata',
            event: 'event_property',
            utm: 'session_properties',
        }[type];

        if (groupByType) {
            types.add(groupByType);
        }

        if (DisplayName) {
            values.add(DisplayName);
        }

        if (includeMetadataGroup) {
            metadataGroups.add(group);
        }

        if (type === 'event') {
            if (DEVICE_PROP_FIELDS.includes(field) && kind === 'automatic') {
                hasGroupByDeviceProp = true;
                eventPropTypes.add('device');
            } else {
                eventPropTypes.add('custom');
            }
        } else if (type === 'historical') {
            eventPropTypes.add(kind);
        } else if (type === 'utm') {
            eventPropTypes.add(DisplayName);
        }
    });

    const groupByTypes = {
        hasGroupByDeviceProp,
        groupByType: Array.from(types)
            .sort()
            .toString(),
        groupByEventPropertyValueType: Array.from(eventPropTypes)
            .sort()
            .toString(),
        groupByValues: Array.from(values)
            .sort()
            .toString(),
    };

    if (includeMetadataGroup) {
        groupByTypes.groupByMetadataGroup = Array.from(metadataGroups)
            .sort()
            .toString();
    }

    return groupByTypes;
}

export function getFilterTypes (
    targetGroups,
    globalFilters = undefined,
    filterByType = undefined,
    includeMetadata = false,
) {
    const filterTypes = [];
    const metadataGroups = [];
    const metadataKinds = [];
    const getFilterFunc = (filter) => {
        if (filterByType && filterByType !== filter.filterType) {
            return;
        }

        if (includeMetadata && metadataGroups.indexOf(filter.group) === -1) {
            metadataGroups.push(filter.group);
        }

        if (includeMetadata && metadataKinds.indexOf(filter.kind) === -1) {
            metadataKinds.push(filter.kind);
        }

        if (filterTypes.indexOf(filter.filterType) === -1) {
            filterTypes.push(filter.filterType === FilterType.Utm ? 'session_properties' : filter.filterType);
        }
    };

    targetGroups.forEach((targetGroup) => {
        targetGroup.targets.forEach((target) => {
            target.filters.forEach(getFilterFunc);
        });
    });

    if (globalFilters) {
        globalFilters.forEach(getFilterFunc);
    }

    const filterTypesObj = {
        hasFilter: !!filterTypes.length,
        hasFilterTypes: filterTypes.toString(),
    };

    if (includeMetadata) {
        filterTypesObj.filterMetadataGroups = metadataGroups.toString();
        filterTypesObj.filterMetadataKinds = metadataKinds.toString();
    }

    return filterTypesObj;
}

function getFormulaInfo (formulas = [], targetGroups = []) {
    // The 'name' property is omitted to ensure the protection of potentially sensitive customer information.
    const targetGroupsRemovedNames = targetGroups.map((targetGroup) => {
        const { name, ...rest } = targetGroup;

        return rest;
    });

    const hasFormulas = formulas && formulas.length > 0;
    const formulasRaw = [];
    let hasSUM = false;
    let hasSUMEACH = false;
    let hasDIV = false;

    formulas.forEach((formula) => {
        formulasRaw.push(buildFormulaEquationString(formula, targetGroupsRemovedNames));
        hasSUM = hasSUM || formula.type === 'sum';
        hasDIV = hasDIV || formula.type === 'ratio';
        hasSUMEACH = hasSUMEACH || formula.type === 'relativePercentage';
    });

    return {
        hasFormulas,
        numFormulas: formulas.length,
        hasSUM,
        hasSUMEACH,
        hasDIV,
        formulasRaw,
    };
}

/**
 * Return subset of object properties whose keys take the form of
 * `groupValue${i}` with consecutive `i` starting from zero. This function will
 * even recognize falsy, undefined, and inherited properties.
 */
export function pickGroupValues (data) {
    const groupObject = {};
    let i = 0;
    let key;

    // eslint-disable-next-line no-cond-assign
    while ((key = `groupValue${i++}`) in data) {
        groupObject[key] = data[key];
    }

    return groupObject;
}

/**
 * Return array of values from object whose keys take the form of
 * `groupValue${i}` with consecutive `i` starting from zero. This function will
 * even recognize falsy, undefined, and inherited properties.
 */
export function pickOrderedGroupValues (data) {
    const groupArray = [];
    let i = 0;
    let key;

    if (data) {
        // eslint-disable-next-line no-cond-assign
        while ((key = `groupValue${i++}`) in data) {
            groupArray.push(data[key]);
        }
    }

    return groupArray;
}

/**
 * A best-effort attempt to create arrays of unique values against which DX data
 * is grouped, without network requests.  For segments and date ranges, this
 * function always returns. Some group-by configs can also be resolved locally,
 * but others require an metadata aggregation call.  In those cases, that
 * particular element of the return value is null, and gets resolved in the
 * agg-runner mixin.
 */
export function createGroupValues (globals) {
    if (get(globals, 'segments.length', 0) > 1) {
        return [uniq(globals.segments.map((segment) => segment.value.id))];
    }

    if (get(globals, 'dateConfigs.length') > 1) {
        return [
            globals.dateConfigs.map((dateConfig) => {
                let params;
                if (dateConfig.isPrimary) {
                    params = { dateConfig, primaryDateConfig: dateConfig };
                } else {
                    const primaryDateConfig = globals.dateConfigs.find((el) => el.isPrimary);
                    params = { dateConfig, primaryDateConfig };
                }

                return dateConfig.value.dateRange.type === 'Custom'
                    ? buildDateConfigGroup(params)
                    : dateConfig.value.dateRange.type;
            }),
        ];
    }

    const { groupings = [] } = globals;
    if (groupings.length !== 1) {
        return [null];
    }

    return groupings.map((grouping) => {
        const groupByMetadataKind = grouping.configuration.kind;
        const groupByMetadataType = grouping.type;
        if (
            groupByMetadataKind &&
            groupByMetadataKind !== 'automatic' &&
            [MetadataTypeId.Event, MetadataTypeId.Historical, MetadataTypeId.Utm].includes(groupByMetadataType)
        ) {
            const eventPropEnums = uniq(grouping.configuration.uniqueValues);

            return eventPropEnums ? eventPropEnums.concat('(none)') : ['(none)'];
        }

        return null;
    });
}

export function getTargetGroupType (targetGroup) {
    if (!targetGroup.targets?.length) {
        return null;
    }

    const { type } = targetGroup.targets[0];
    switch (type) {
        case EventName.Feature:
        case EventName.Page:
        case EventName.TrackEvent:
            return 'event';
        case EventName.Guide:
            return 'guideViews';
        default:
            return 'anyActivity';
    }
}

export function getEventTypeCounts (targetGroup) {
    const counts = {
        [EventName.App]: 0,
        [EventName.Feature]: 0,
        [EventName.Page]: 0,
        [EventName.TrackEvent]: 0,
        [EventName.Guide]: 0,
    };
    if (!targetGroup.targets?.length) {
        return counts;
    }

    targetGroup.targets.forEach(({ type }) => {
        counts[type]++;
    });

    return counts;
}

export function getAppIdsByType (targetGroup, targetEntityMap, eventType) {
    const appSet = new Set();
    targetGroup.targets.forEach((target) => {
        if (target.type === eventType) {
            const appId = get(targetEntityMap, [target.type, target.id, 'appId']);
            appSet.add(appId);
        }
    });

    return Array.from(appSet);
}

function getTypeLabel (type) {
    switch (type) {
        case EventName.App:
            return 'apps';
        case EventName.Feature:
            return 'Features';
        case EventName.Page:
            return 'Pages';
        case EventName.TrackEvent:
            return 'Track Events';
        case EventName.Guide:
            return 'guides';
    }

    return '';
}

export function checkForDisabledEventTypes (eventTypeCounts, disabledTypes) {
    for (const type of disabledTypes) {
        if (eventTypeCounts[type] > 0) {
            return { disabled: true, disabledReason: `Not available for ${getTypeLabel(type)}` };
        }
    }

    return { disabled: false };
}

function base26divmod (i) {
    const mod = i % 26;
    const div = (i - mod) / 26;

    if (mod === 0) {
        return [div - 1, mod + 26];
    }

    return [div, mod];
}

/**
 * Converts numeric indexes into Excel-style alphabetic IDs.
 *
 * A "zero" digit doesn't exist in the alphabet, so the output of this function isn't true "base 26."
 * Think of the output more like column labels in Excel.
 *
 * @param {Number} i Zero-based index to convert
 * @returns Base-26 alphabetic ID
 */
function indexToAlphabeticID (i) {
    ++i; // Start at index 1
    const chars = [];

    while (i > 0) {
        const [div, mod] = base26divmod(i);
        chars.unshift(String.fromCharCode(64 + mod));
        i = div;
    }

    return chars.join('');
}

export function getTargetGroupLabel ({ targetGroup, targetGroupIndex, measure, longOrShortForm }) {
    const name = targetGroup?.name?.value;
    if (name && name.length > 0) {
        if (measure && longOrShortForm === 'long') {
            const measureLabel = getLabelFromTargetGroupMeasure(measure);

            return `${name} ${measureLabel}`;
        }

        return name;
    }

    // This is for legacy data explorer reports or event groups where custom names have not yet been added
    const alphaId = indexToAlphabeticID(targetGroupIndex);
    if (longOrShortForm === 'short') return alphaId;

    if (measure && longOrShortForm === 'long') {
        const measureLabel = getLabelFromTargetGroupMeasure(measure);

        return `Group ${alphaId} ${measureLabel}`;
    }

    return `Group ${alphaId}`;
}

/**
 * Remove targets from a DX report definition based on what is selected to be
 * visible in the chart.
 *
 * The conditions for filtering are nuanced:
 *
 * - If a given target group is referenced in a visible formula, no targets can
 * be removed from the group. All of them are necessary to compute the formula
 * result.
 *
 * - With a small enough target count, each target gets its own aggregation
 * call.
 *
 * - "Any Activity" queries are always grouped into a single call. Filtering is
 * always necessary.
 */
export function filterSourceTargetsByRowSelections (targetGroups, globals, rowSelections) {
    const chartIds = Object.keys(pickBy(rowSelections));
    const formulas = migrateLegacyFormulas(globals.formulas) || [];

    const formulaGroupIds = new Set();
    for (const formula of formulas) {
        const groupIds = getFormulaGroupIds(formula);
        if (chartIds.some((chartId) => chartId.startsWith(formula.id))) {
            groupIds.forEach((groupId) => formulaGroupIds.add(groupId));
        }
    }

    targetGroups.forEach((targetGroup) => {
        const { targets, id } = targetGroup;

        if (formulaGroupIds.has(id.value)) return;

        if (targets.length > MAX_SINGLE_TARGETS || get(targets, '[0].type') === 'anyActivity') {
            const filteredTargets = targets.filter((target) => {
                const aggIdRegex = new RegExp(`^${targetGroup.id.value}-${target.id}-*`);

                return chartIds.some((chartId) => aggIdRegex.test(chartId));
            });
            targetGroup.targets = filteredTargets;
        }
    });

    return formulaGroupIds.size === 0;
}

export const getFilterIdentifier = ({ field, filterType, kind, group = '' }) => {
    if (filterType === FilterType.Visitor && field === 'id') {
        return 'visitorId';
    }
    if (filterType === FilterType.Account && field === 'id') {
        return 'accountId';
    }

    if (
        filterType === FilterType.ParentAccount ||
        filterType === FilterType.Account ||
        filterType === FilterType.Visitor
    ) {
        return `${kind}.${group}.${field}`;
    }

    if (filterType === FilterType.Location && field === 'region') return 'country_region';
    if (filterType === FilterType.PageParameter) return PageParameterId;
    if (filterType === FilterType.EventTopLevel || filterType === FilterType.Location) return `${field}`;

    return `${field}_${filterType}_${kind}`;
};

export function getFilterTooltip (filters) {
    const filterStrings = filters.map((filter) => {
        let tooltip = `${filter.name ? filter.name : filter.field} ${filter.operator}`;
        if (filter.comparisonValues?.length) {
            tooltip += ` ${filter.comparisonValues.join(', ')}`;
        }

        return tooltip;
    });

    return filterStrings.join('\n');
}

export function getTargetIcon ({ kind, platform }) {
    if (kind) {
        let type = lowerFirst(kind);
        if (kind === 'AIAgent') {
            type = 'agentic';
        }

        return { type };
    }

    return platform
        ? {
            type: getAppIconType(platform),
            fill: platform === 'extension' ? 'currentColor' : 'none',
        }
        : null;
}

export function getGroupId (point) {
    // The map gives us 'null' and 'undefined' when they would
    // otherwise become empty ('') in the join operation
    return (
        pickOrderedGroupValues(point)
            .map(String)
            .join('_') || 'null'
    );
}

export function beautifyOsVersion (version, appId) {
    const appMap = getActiveApplicationsMap();
    const app = appMap[appId];

    if (app?.platform.toLowerCase() === 'android' && version) {
        return `${version} (${app.platform} ${apiLevelToVersion(version) || `API-${version}`})`;
    }

    return version;
}

function getQueryBuilderDisabledSectionTooltip (
    prefix,
    {
        isGloballyDisabled,
        exceedsComparableTargetsLimit,
        hasMultipleSegments,
        hasGroupings,
        hasMultipleDateConfigs,
        hasFormulas,
    },
) {
    if (isGloballyDisabled) {
        return '';
    }
    if (exceedsComparableTargetsLimit) {
        return `${prefix} while comparing more than 5 objects.`;
    }
    if (hasMultipleSegments) {
        return `${prefix} while comparing segments.`;
    }
    if (hasGroupings) {
        return `${prefix} while grouping.`;
    }
    if (hasMultipleDateConfigs) {
        return `${prefix} while comparing date ranges.`;
    }
    if (hasFormulas) {
        return `${prefix} while using formulas.`;
    }

    return '';
}

export function getQueryBuilderDisabledSectionConfig (section, states, allowFormulasAndGroupBy = false) {
    const { isGloballyDisabled, exceedsComparableTargetsLimit } = states;
    let tooltipPrefix = '';
    let tooltip = '';
    let disabled = false;

    switch (section) {
        case 'segment':
            disabled = some(omit(states, 'hasMultipleSegments'), Boolean);
            tooltipPrefix = exceedsComparableTargetsLimit ? "Can't add a third segment" : "Can't add a second segment";
            break;
        case 'groupBy':
            disabled = some(
                omit(states, allowFormulasAndGroupBy ? ['hasFormulas', 'hasGroupings'] : ['hasGroupings']),
                Boolean,
            );
            tooltipPrefix = "Can't add a grouping";
            break;
        case 'dateRange':
            disabled = some(omit(states, 'hasMultipleDateConfigs'), Boolean);
            tooltipPrefix = "Can't add a second date range";
            break;
        case 'formula':
            disabled = some(
                omit(states, allowFormulasAndGroupBy ? ['hasFormulas', 'hasGroupings'] : ['hasFormulas']),
                Boolean,
            );
            tooltipPrefix = "Can't add formulas";
            break;
    }

    if (disabled) {
        tooltip = getQueryBuilderDisabledSectionTooltip(tooltipPrefix, states);
    }

    return {
        disabled,
        globallyDisabled: isGloballyDisabled,
        tooltip,
    };
}

/**
 * Generates the series ID for ONLY standard, single-target series lines.
 *
 * The series IDs in rowSelections take three different forms:
 *
 * - For standard, single-target series: concatenation of the target group ID,
 * target ID, and the group value.
 *
 * - For aggregate formulas: just the formula ID.
 *
 * - For iterative (sumeach) formulas: concatenation of the formula ID and the
 * associated target ID.
 * @param {DxGroupValuesMap|null} [groupValues=null] - The group values map for the series
 */
export function getSeriesId (series, groupValues = null) {
    const { target, targetGroupId } = series;
    const groupId = groupValues
        ? Object.values(groupValues)
            .map(String)
            .join('_') || 'null'
        : getGroupId(series);

    return `${targetGroupId}-${target?.id}-${groupId}`;
}

export function fixDuplicateDraftSegmentIds (segments) {
    if (!segments) return segments;
    const allSegmentIds = segments.map((segment) => segment.value.id);
    const segmentIdSet = new Set(allSegmentIds);

    // The segment index matters, so use the full list of segment ids to iterate backwards (fixing the last index of the duplicated segment id)
    for (let i = allSegmentIds.length - 1; i >= 0; i--) {
        const currentSegmentId = allSegmentIds[i];
        // For now we only care about fixing duplicate draft segment ids
        if (isDraftSegment(currentSegmentId)) {
            if (allSegmentIds.indexOf(currentSegmentId) < i) {
                // Is the current instance of the segment NOT the first one in the array? Okay, we need to assign a new id.
                const newSegmentId = generateNewDraftSegmentId([...segmentIdSet]);
                segments[i].value.id = newSegmentId;
                segmentIdSet.add(newSegmentId);
            }
        }
    }

    return segments;
}

export function stripTargetsGroups (targetGroups) {
    return targetGroups.map((targetGroup) => {
        const { targets } = targetGroup;

        return {
            ...targetGroup,
            targets: targets.map((target) => {
                const { id, type, filters } = target;

                return {
                    id,
                    type,
                    filters: filters?.length ? filters : [],
                };
            }),
        };
    });
}

export function expandSegmentsToFullObjects (segments, segmentById) {
    if (!segments) return segments;

    const wholeSegmentObjects = segments.map((segment) => {
        const fullSegmentObject = isDraftSegment(segment.value?.id) ? segment.value : segmentById(segment.value.id);

        return {
            valid: segment.valid,
            value: fullSegmentObject ?? segment.value,
        };
    });

    return wholeSegmentObjects;
}

export function stripNonessentialSegmentData (segments) {
    if (!segments) return segments;

    const updatedList = [];
    for (let i = 0; i < segments.length; i++) {
        // Only remove data from non-draft segments
        if (!isDraftSegment(segments[i].value?.id)) {
            const trimmedSegment = {
                valid: segments[i].valid,
                value: {
                    id: segments[i].value.id,
                },
            };
            if (segments[i].value.createdByUser?.username) {
                trimmedSegment.value.createdByUser = { username: segments[i].value.createdByUser.username };
            }
            updatedList.push(trimmedSegment);
        } else {
            updatedList.push(segments[i]);
        }
    }

    return updatedList;
}

export function buildYaxisConfig (config, percentageOrTime) {
    return defaultsDeep({}, config, {
        showEmpty: false,
        alignTicks: true,
        tickPixelInterval: 72,
        title: {
            text: null,
            style: {
                fontSize: '14px',
                textOverflow: 'ellipsis',
            },
        },
        labels: {
            formatter () {
                const label = this.axis.defaultLabelFormatter.call(this);

                if (percentageOrTime === 'percentage') {
                    return formatNumberAsPercentage(this.value);
                }
                if (percentageOrTime === 'time') {
                    return prettyTime(this.value * 60 * 1000);
                }

                return label;
            },
        },
        opposite: !!percentageOrTime,
        allowDecimals: percentageOrTime === 'percentage',
        softMax: percentageOrTime === 'percentage' ? 0.01 : undefined,
    });
}

export function getWeeklyVisitorsParams (appId) {
    return {
        globals: {
            dateConfigs: [
                {
                    value: {
                        dateRange: {
                            type: DateConfigTypes.Last90Days,
                        },
                        period: TimeSeriesPeriod.Weekly,
                        interval: TimeInterval.Week,
                    },
                    isPrimary: true,
                    valid: true,
                },
                {
                    value: {
                        dateRange: {
                            type: 'Same 90 Days Last Year',
                            key: 'samePeriodLastYear',
                            offset: -395,
                        },
                        period: TimeSeriesPeriod.Weekly,
                        interval: TimeInterval.Week,
                    },
                    valid: true,
                },
            ],
            groupings: [],
            segments: [
                {
                    value: {
                        id: 'everyone',
                        name: 'Everyone',
                        definition: 'everyone',
                        default: true,
                    },
                    valid: true,
                },
            ],
            filters: [],
            formulas: [],
            account: {
                id: '',
                text: 'All Accounts',
                isParentAccount: false,
            },
        },
        targetGroups: [
            createNewTargetGroupObject(
                [{ id: appId, type: EventName.App, filters: [] }],
                MEASURE_OPTIONS.NUM_VISITORS.name,
            ),
        ],
    };
}

function getVisitorSignalParams (appId, measure, segment) {
    const segmentValue = segment || {
        id: 'everyone',
        name: 'Everyone',
        definition: 'everyone',
        default: true,
    };

    return {
        globals: {
            dateConfigs: [
                {
                    value: {
                        dateRange: { type: DateConfigTypes.Last7Days },
                        period: TimeSeriesPeriod.Daily,
                        interval: TimeInterval.Day,
                    },
                    isPrimary: true,
                    valid: true,
                },
                {
                    value: {
                        dateRange: { type: 'Previous 7 Days', key: 'previousPeriod', offset: -14 },
                        period: TimeSeriesPeriod.Daily,
                        interval: TimeInterval.Day,
                    },
                    valid: true,
                },
            ],
            groupings: [],
            segments: [
                {
                    value: segmentValue,
                    valid: true,
                },
            ],
            filters: [],
            formulas: [],
            account: {
                id: '',
                text: 'All Accounts',
                isParentAccount: false,
            },
        },
        targetGroups: [
            createNewTargetGroupObject(
                [{ id: appId, type: EventName.App, filters: [] }],
                measure,
            ),
        ],
    };
}

export function getVisitorAdoptionParams (appId, segment) {
    return getVisitorSignalParams(appId, MEASURE_OPTIONS.NUM_VISITORS.name, segment);
}

export function getVisitorEngagementParams (appId, segment) {
    return getVisitorSignalParams(appId, MEASURE_OPTIONS.AVERAGE_MINUTES_PER_VISITOR.name, segment);
}

export function getTop10PagesParams (pages) {
    return {
        globals: {
            dateConfigs: [
                {
                    value: {
                        dateRange: {
                            type: DateConfigTypes.Last30Days,
                        },
                        period: TimeSeriesPeriod.Daily,
                        interval: TimeInterval.Day,
                    },
                    isPrimary: true,
                    valid: true,
                },
            ],
            groupings: [],
            segments: [
                {
                    value: {
                        id: 'everyone',
                        name: 'Everyone',
                        definition: 'everyone',
                        default: true,
                    },
                    valid: true,
                },
            ],
            filters: [],
            formulas: [],
            account: {
                id: '',
                text: 'All Accounts',
                isParentAccount: false,
            },
        },
        targetGroups: [
            createNewTargetGroupObject(
                pages.map((page) => ({
                    filters: [],
                    id: page.id,
                    type: EventName.Page,
                })),
                MEASURE_OPTIONS.TOTAL.name,
            ),
        ],
    };
}

export function getGuideGoalParams (segmentA, segmentB, countableType, countableId) {
    return {
        globals: {
            dateConfigs: [
                {
                    value: {
                        dateRange: {
                            type: DateConfigTypes.Last30Days,
                        },
                        period: TimeSeriesPeriod.Daily,
                        interval: TimeInterval.Day,
                    },
                    isPrimary: true,
                    valid: true,
                },
            ],
            groupings: [],
            segments: [
                {
                    value: segmentA,
                    valid: true,
                },
                {
                    value: segmentB,
                    valid: true,
                },
            ],
            filters: [],
            formulas: [],
            account: {
                id: '',
                text: 'All Accounts',
                isParentAccount: false,
            },
        },
        targetGroups: [
            createNewTargetGroupObject(
                [
                    {
                        filters: [],
                        id: countableId,
                        type: EventName[countableType],
                    },
                ],
                MEASURE_OPTIONS.TOTAL.name,
            ),
        ],
    };
}

export function getMetadataInfoFromField (metadata, field) {
    if (!metadata || typeof metadata !== 'object') return null;
    const queue = [{ node: metadata, parentKey: null }];

    while (queue.length) {
        const { node, parentKey } = queue.shift();

        for (const [key, value] of Object.entries(node)) {
            if (key === field) return { key: parentKey, value };
            if (value && typeof value === 'object') queue.push({ node: value, parentKey: key });
        }
    }

    return undefined;
}

export function fillMissingFilterValues (filters, metadata) {
    filters.forEach((filter) => {
        const metadataInfo = getMetadataInfoFromField(metadata, filter.field);
        if (metadataInfo) {
            if (!filter.group && metadataInfo.key) {
                filter.group = metadataInfo.key;
            }
            if (!filter.name) {
                if (metadataInfo.value && metadataInfo.value.DisplayName) {
                    filter.name = metadataInfo.value?.DisplayName;
                }
            }
        }
    });

    return filters;
}
